// packages
import cx from 'classnames';
import { entries, map, Map, set, Set } from 'd3-collection';
import { GeoPath, GeoIdentityTransform, GeoProjection } from 'd3-geo';
import { scaleSqrt, extent } from 'd3';
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { Feature, Point } from 'geojson';
import { Component, h } from 'preact';
import bboxPolygon from '@turf/bbox-polygon';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import sumValues from '../utils/sum-values';

// components
import CanvasLayer from './CanvasLayer';
// import MapLegend from './MapLegend';
import MapText from './MapText';
import Places from './Places';
import CartogramCountyLabels from './CartogramCountyLabels';
// import Portal from './Portal';
import SvgLayer from './SvgLayer';
import Tooltip from './Tooltip';
import TooltipTable from './TooltipTable';
import { showBubbleMap, DDHQestimatedvotes } from '../endpoints';

// internal
import clamp from '../utils/clamp';
import * as colors from '../colors';
import find from '../utils/find';
import getPoint from '../utils/get-point';
import prepareTopoJSON from '../utils/prepare-topojson';
import getDistance from '../utils/get-distance';
import { Topology } from 'topojson-specification';
import data from '../../../data/data.json'

interface LayeredMapProps {
  projection: GeoProjection | GeoIdentityTransform;
  topology: Topology;
  precinctsReporting: number;
  totalVotes: number;
  source: string;
  candidatesRender: { [key: string]: any };
}

interface LayeredMapState {
  activeGeometry: Feature | null;
  candidatesOnMap: Set;
  geos: any;
  width: number;
  height: number;
  x: number;
  y: number;
  activeBtn: boolean;
  includesTie: boolean;
  typeMap: string;
}

const tooltipPush = 20;
const cartogramPosition = data['breakdown_cartogram_position'];
const colorOrder = [
  '#EF564A', // R
  '#3cc687', // G
  '#bdbdbd', // votes left to count
  '#be9304', // L
  '#1755c5' // D
];
const countiesToShow = [
  {name: 'Harris'},
  {name: 'Dallas'},
  {name: 'Tarrant'}, 
  {name: 'Bexar'}, 
  {name: 'Travis'}, 
  {name: 'Collin'}, 
  {name: 'Denton'}, 
  {name: 'Fort Bend'}, 
  {name: 'Williamson'}, 
  {name: 'El Paso'}
];
const mapTranslateValue = 8;

const relocateTooltip = (tooltip, x, y) => {
  const { height, width } = tooltip.getBoundingClientRect();

  const innerWidth = window.innerWidth;
  const innerHeight = window.innerHeight;

  const left = clamp(
    x - width / 2,
    tooltipPush,
    innerWidth - width - tooltipPush
  );
  const top =
    innerHeight > y + tooltipPush + height
      ? y + tooltipPush
      : y - height - tooltipPush;

  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

class LayeredMap extends Component<LayeredMapProps, LayeredMapState> {
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  tooltip!: HTMLElement;
  candidateMap!: Map<any>;
  countyMap!: Map<any>;
  embedType: string;
  candidatesRender: { [key: string]: any };

  constructor() {
    super();

    this.state = {
      activeGeometry: null,
      candidatesOnMap: set(),
      geos: {},
      width: 0,
      height: 0,
      includesNoResult: false,
      includesTie: false,
      typeMap: 'categorical',
      activeBtn: 'categorical',
      x: 0,
      y: 0,
      rRange: [],
      rScale: null,
      countiesToShow: [],
      activePieCounty: null,
      border: null,
    };

    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onDocumentTouch = this.onDocumentTouch.bind(this);
    this.getTooltipRef = this.getTooltipRef.bind(this);
    this.canvasRenderer = this.canvasRenderer.bind(this);

    this.xScale = scaleLinear();
    this.yScale = scaleLinear();
  }

  getTooltipRef(el: HTMLElement) {
    this.tooltip = el;
  }

  setUpCountyMap(counties: any) {
    this.countyMap = map(counties, d => d.fips);
  }

  setUpCandidateMap(candidates: any) {
    this.candidateMap = map(candidates, d => d.id);
  }

  handleClick = param => event => {
    this.setState({
      typeMap: param,
      activeBtn: param,
    });
  };

  onMouseMove(event: MouseEvent) {
    const { typeMap } = this.state;
    const { clientX: x, clientY: y, target } = event;
    const point = getPoint(target as HTMLElement, x, y);

    if(typeMap == 'categorical') {
      const coords = [this.xScale.invert(point[0]), this.yScale.invert(point[1])];

      const { collection, index } = this.state.geos;
  
      const found = index
        .search(...coords, ...coords)
        .map((i: number) => collection.features[i]);
  
      const activeGeometry = find(
        found,
        (feature: Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) =>
          booleanPointInPolygon(coords, feature)
      );
  
      let activeCounty = null;
  
      if (activeGeometry) {
        const { fips } = activeGeometry.properties;
        activeCounty = this.countyMap.get(fips);
      }
  
      this.setState({ activeCounty, activeGeometry }, () =>
        relocateTooltip(this.tooltip, x, y)
      );
    } else { // when breakdown cartogram is selected
      // distance from pie center is within radius => cursor is on a pie
      const activePieCounty = [];

      cartogramPosition.forEach(p => {
        // see if the distance from pie center is within radius
        const distance = getDistance(p.center[0], p.center[1], point[0] - mapTranslateValue, point[1])
        if (distance <= p.radius) {
          activePieCounty.push(p);
        }
      })

      let activeCounty = null;
  
      if (activePieCounty[0] != undefined) {
        const fips = activePieCounty[0].fips;
        activeCounty = this.countyMap.get(fips);
      }

      this.setState({ 
        activeCounty,
        activePieCounty: activePieCounty[0]
      }, () =>
        relocateTooltip(this.tooltip, x, y)
      );
    }
  } 

  onMouseLeave() {
    this.setState({ activeCounty: null, activeGeometry: null, activePieCounty: null });
  }

  onDocumentTouch(e) {
    if (!this.base.contains(e.target)) this.onMouseLeave();
  }

  drawBaseMap(ctx, geos, geoPath, strokeColor) {
    geos.collection.features.forEach((feature: Feature) => {
      const { fips } = feature.properties;

      ctx.beginPath();
      geoPath(feature);

      ctx.fillStyle = colors.NEUTRAL;
      ctx.fill();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.closePath();
    });
  }

  drawRoadsLayer(ctx, geos, geoPath) {
    geos.roads.features.forEach((feature: Feature) => {
      if (feature.properties.level !== 'Interstate') return;
      ctx.beginPath();
      geoPath(feature);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (feature.properties.level === 'Interstate') {
        ctx.strokeStyle = 'rgba(0, 0, 0, .12)';
        ctx.lineWidth = 1;
      } else {
        ctx.strokeStyle = 'rgba(0, 0, 0, .08)';
        ctx.lineWidth = 0.8;
      }

      ctx.stroke();
      ctx.closePath();
    });
  }

  drawPlacesLayer(ctx, geos,geoPath) {
    let places_mappable = geos.places;

    geos.places = places_mappable;

    geos.places.features.forEach((feature: Feature<Point>) => {
      const [x, y] = geoPath.centroid(feature);

      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2, true);
      ctx.strokeStyle = 'rgba(33, 33, 33, .75)';
      ctx.lineWidth = 1.25;
      ctx.stroke();
      ctx.closePath();
    });
  }

  drawUrbanAreasLayer(ctx, geos, geoPath) {
    geos.urban_areas.features.forEach((feature: Feature) => {
      ctx.beginPath();
      geoPath(feature);

      ctx.fillStyle = 'rgba(153, 160, 175, 0.2)';
      ctx.fill();
      ctx.closePath();
    });
  }

  canvasRenderer(ctx: CanvasRenderingContext2D, geoPath: GeoPath) {
    const { geos, width, height, typeMap, rScale } = this.state;
    const { candidatesRender, embedType, projection } = this.props;

    ctx.clearRect(0, 0, width, height);
    ctx.translate(mapTranslateValue, 0)

    if (!geos.collection) return;

    const candidatesOnMap = set();
    let includesNoResult = false;
    let includesTie = false;

    if (typeMap == 'categorical') {
      geos.collection.features.forEach((feature: Feature) => {
        const { fips } = feature.properties;
        const county = this.countyMap.get(fips);

        let fillStyle, lean;

        if (county.total_votes <= 0) {
          includesNoResult = true;
          fillStyle = colors.NEUTRAL;
        } else {
          const sortedResults = entries(county.results).sort(
            (a, b) => b.value - a.value
          );

          const leader = sortedResults[0];
          const second = sortedResults[1];

          if (leader.value === second.value) {
            includesTie = true;
            fillStyle = colors.TIE;
          } else {
            const leadingId = leader.key;
            candidatesOnMap.add(leadingId);

            const leader_pct = leader.value/county.total_votes
          const second_pct = second.value/county.total_votes

          const margin = (leader_pct-second_pct)

          if (margin.toFixed(2) < 0.10) {
            lean = true
          } else {
            lean = false
          }

            const { id } = this.candidateMap.get(leadingId);
            const result = candidatesRender.filter(cand => cand.id == id);
            fillStyle = lean == true ? result[0]['lean_color']: result[0]['color'];
            // fillStyle = result[0]['color'];
          }
        }

        ctx.beginPath();
        geoPath(feature);

        ctx.fillStyle = fillStyle;
        ctx.fill();
        ctx.closePath();
      });

      ctx.beginPath();
      geoPath(geos.collection);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(255, 255, 255, .6)'; //255, 255, 255
      ctx.lineWidth = 0.8;

      ctx.stroke();
      ctx.closePath();

      this.drawRoadsLayer(ctx, geos, geoPath);
      this.drawPlacesLayer(ctx, geos, geoPath);
      this.drawUrbanAreasLayer(ctx, geos, geoPath);
    }

    if (typeMap == 'breakdown') {
      // base map
      this.drawBaseMap(ctx, geos, geoPath, colors.NEUTRAL);

      // pie
      // calculate cartogram positions
      cartogramPosition.map(p => {
        // get projected coordinates
        const coordinates = [p.recal_x, p.recal_y];
        const county = this.countyMap.get(p.fips);
        p.center = projection(coordinates);
        p.radius = rScale(county[DDHQestimatedvotes]);
      })

      cartogramPosition.forEach(p => {
        const county = this.countyMap.get(p.fips);
        const sortedResults = entries(county.results);
        sortedResults.map(r => {
          const candidate = candidatesRender.find(cand => cand.id == r.key);
          r.color = candidate.color;
        })
        const votesLeft = county[DDHQestimatedvotes] - county.total_votes;
        sortedResults.push({key: 'votesLeftToCount', value: votesLeft, color: '#bdbdbd'}); 
        sortedResults.sort((a, b) => colorOrder.indexOf(a.color) - colorOrder.indexOf(b.color));

        let currentAngle = -Math.PI / 2;

        for (const candidate of sortedResults) { 
          //calculating the angle the slice (portion) will take in the pie chart
          let portionAngle = candidate.value > 0
            ? (candidate.value / county[DDHQestimatedvotes]) * 2 * Math.PI
            : 0;

          //drawing an arc and a line to the center to differentiate the slice from the rest
          ctx.beginPath();
          ctx.arc(p.center[0], p.center[1], p.radius, currentAngle, currentAngle + portionAngle);
          currentAngle += portionAngle;
          ctx.lineTo(p.center[0], p.center[1]);
          //filling the slices with the corresponding candidates's color
          ctx.fillStyle = candidate.color
          ctx.fill();
        }
      })
    }
    this.setState({ candidatesOnMap, includesNoResult, includesTie });
  }

  componentWillReceiveProps(nextProps) {
    const { candidates, counties } = nextProps;

    this.setUpCandidateMap(candidates);
    this.setUpCountyMap(counties);
  }

  getContainerDimensions() {
    const base = this.base!;
    const rawWidth = base.offsetWidth;

    this.setState({ width });
  }

  componentDidMount() {
    const { candidates, counties, candidatesRender, projection } = this.props;

    this.setUpCandidateMap(candidates);
    this.setUpCountyMap(counties);

    const base = this.base!;
    const rawWidth = Math.floor(base.clientWidth);

    const { topology } = this.props;
    const geos = prepareTopoJSON(topology);

    const { bbox, heightRatio } = geos;
    const rawHeight = Math.floor(rawWidth * heightRatio);

    const width = rawWidth;
    const height = rawHeight;

    // adjust map width and height so that El Paso won't be cut off
    const mapWidth = width - mapTranslateValue;
    const mapHeight = height;

    this.props.projection
      .fitSize([mapWidth, mapHeight], bboxPolygon(bbox))

    this.xScale
      .domain([bbox[0], bbox[2]])
      .range([mapTranslateValue, width]);
    this.yScale
      .domain([bbox[1], bbox[3]])
      .range([height, 0]);

    // calculate rScale
    const all_Leader_sizes = [];

    geos.collection.features.forEach((feature: Feature) => {
      const { fips } = feature.properties;
      const county = this.countyMap.get(fips);
      all_Leader_sizes.push(county[DDHQestimatedvotes]);
    })

    const e = extent(all_Leader_sizes);
    const rRange = [
      2 / 620 * rawWidth,
      50 / 620 * rawHeight
    ];
    const rScale = scaleSqrt()
      .range(rRange)
      .domain(e);

    // large counties to show county labels on cartogram
    countiesToShow.map(county => {
      county.coordinates = [
        cartogramPosition.find(d => d.county == county.name)?.recal_x,
        cartogramPosition.find(d => d.county == county.name)?.recal_y
      ]
    });

    // space for annotation
    const border = {
      elPaso: null,
      marverick: null
    };
    // El Paso coordinates
    const elPaso = cartogramPosition.find(d => d.county == 'El Paso');
    const elPasoCoordinates = projection([elPaso.recal_x, elPaso.recal_y]);
    border.elPaso = elPasoCoordinates;

    // Maverick coordinates
    const maverick = cartogramPosition.find(d => d.county == 'Maverick');
    const maverickCoordinates = projection([maverick.recal_x, maverick.recal_y]);
    border.marverick = maverickCoordinates;

    document.addEventListener('touchstart', this.onDocumentTouch, false);
    
    this.setState({ geos, width, height, rRange, rScale, countiesToShow, border });

  }

  render() {
    const {
      candidates,
      projection,
      precinctsReporting,
      totalVotes,
      candidatesRender,
      embedType,
      source,
    } = this.props;
    const {
      activeCounty,
      activeBtn,
      activeGeometry,
      candidatesOnMap,
      geos,
      includesNoResult,
      includesTie,
      width,
      height,
      x,
      y,
      typeMap,
      rScale,
      countiesToShow,
      activePieCounty,
      border
    } = this.state;

    return (
      <div id="all-map-stuff">
        <div
          class="layers-map"
          style={{
            boxSizing: 'border-box',
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
          onMouseMove={this.onMouseMove}
          onMouseLeave={this.onMouseLeave}
        >
          {typeMap == 'categorical' ? (
            <SvgLayer
              width={width}
              height={height}
              projection={projection}
              mapTranslateValue={mapTranslateValue}
              render={({ geoPath }) => (
                <g>
                  {geos.places && (
                    <Places geoPath={geoPath} places={geos.places.features} />
                  )}
                  {activeGeometry && (
                    <path
                      fill="none"
                      stroke="black"
                      stroke-width="2"
                      d={geoPath(activeGeometry) || undefined}
                    />
                  )}
                </g>
              )}
            />
          ) : ( // breakdown cartogram
            <SvgLayer
              width={width}
              height={height}
              projection={projection}
              mapTranslateValue={mapTranslateValue}
              render={({ geoPath }) => (
                <g>
                  {countiesToShow && (
                    <CartogramCountyLabels projection={projection} counties={countiesToShow} />
                  )}
                  {activePieCounty && (
                    <circle
                      fill="none"
                      stroke="black"
                      stroke-width="2"
                      cx={activePieCounty.center[0]}
                      cy={activePieCounty.center[1]}
                      r={activePieCounty.radius}
                    />
                  )}
                </g>
              )}
            />
            )
          }
          <CanvasLayer
            totalPrecincts={precinctsReporting}
            totalVotes={totalVotes}
            projection={projection}
            width={width}
            height={height}
            render={this.canvasRenderer}
            mapType={typeMap}
          />
          <Tooltip
            class={cx('tooltip map__tooltip', {
              invisible: !Boolean(activeCounty),
            })}
            tooltipRef={this.getTooltipRef}
          >
            {activeCounty && (
              <TooltipTable
                candidatesRender={candidatesRender}
                candidatesMap={this.candidateMap}
                totalVotes={totalVotes}
                county={activeCounty}
                source={source}
              />
            )}
          </Tooltip>
        </div>
        <div class="below-map">
          <MapText 
            candidates={candidates.filter(({ id }: { id: string }) =>
              candidatesOnMap.has(id)
            )}
            includesNoResult={includesNoResult}
            includesTie={includesTie}
            width={width}
            height={height}
            typeMap={typeMap}
            typeRace={candidates.length === 4 ? 'governor' : 'attorney-general'}
            border={border}
          />

          {showBubbleMap && (
            <div class="map-btn-group">
              <button
                className={
                  activeBtn === 'categorical'
                    ? 'map-button map-button--active'
                    : 'map-button'
                }
                onClick={this.handleClick('categorical')}
              >
                By candidate
              </button>
              <button
                className={
                  activeBtn === 'breakdown'
                    ? 'map-button map-button--active'
                    : 'map-button'
                }
                onClick={this.handleClick('breakdown')}
              >
                Breakdown
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default LayeredMap;
